<template>
  <div class="cms-develop-step-page" :class="titleLayout" v-if="isLoaded">
    <section class="day-title-section">
      <div class="container maxWidth-sm">
        <div style="height: 40px"></div>

        <button class="btn btn-primary-back hidden-print back-button" @click="back" type="button">
          Back
        </button>

        <div class="title-container">
          <div class="title-text">
            <div v-html="titleHtml"></div>
            <hr class="hr-magazine hr-magazine-left hr-magazine-thick hr-magazine-short" />

            <h2 v-if="subtitle">{{ subtitle }}</h2>
            <div style="height: 20px"></div>
          </div>
          <CmsImg class="title-image" v-if="headerImage" :src="headerImage" />
        </div>
        <div style="height: 10px"></div>
      </div>
    </section>

    <CmsBody
      :cmsPage="doc"
      :context="context"
      :completedActivityIds="completedActivityIds"
      @activity-completed="handleActivityCompleted"
      @self-review-completed="handleSelfReviewCompleted"
    />
    <div v-if="canCompleteDay || hasClickedComplete" class="container maxWidth-sm text-center">
      <button
        ref="completeButton"
        v-if="canCompleteDay"
        @click="completeDay()"
        class="read-toolbar-complete-button btn btn-primary btn-lg"
        :disabled="!allActivitiesComplete && !hasClickedComplete"
        type="button"
      >
        <span class="tick-icon glyphicon glyphicon-ok"></span>
        <span class="read-toolbar-complete-button-text">Complete Develop Day</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import { v4 as uuid } from "uuid";
import moment from "dayjs";
import axios from "axios";
import { computed, onBeforeMount, ref } from "vue";
import {
  CompleteActivityActionApiModel,
  CompleteSelfReviewActionApiModel,
  CompleteStepActionApiModel,
  DevelopUserActionApiModel,
  DevelopUserActionKind,
  ProfileApiModel,
  ReadPageActionApiModel,
} from "../../api-models";
import { csContext } from "../../services/cs-context";
import delay from "../../services/utilities/delay";
import { DevelopPlan } from "../../services/cms/develop-plan";
import {
  getDayNumber,
  getStepRoleFromDayNumber,
} from "../../components/develop/develop-plan-models";
import SwipeNavigationInfo from "../../components/swipe-navigation-info";
import CmsImg from "../../components/cms/cms-img.vue";
import CmsBody from "../../components/cms/cms-body.vue";
import cms from "../../services/cms/cms";
import {
  CmsDevelopPage,
  CmsDocument,
  isMandatoryActivity,
  sliceToActivityId,
} from "../../services/cms/cms-type-definitions";

const debug = d("cs.CmsDevelopStepPage");

const props = defineProps<{ doc: CmsDocument; context?: Record<string, any> }>();
const doc = ref<CmsDevelopPage>(props.doc as CmsDevelopPage);

const associatedDevelopPlan = ref<DevelopPlan | null>(null);
const currentProgress = ref(csContext.userProfile.developProgress?.currentStrengthProgress);
const hasClickedComplete = ref(false);
const isLoaded = ref(false);

const headerImage = null;

const isPlanStep = computed(() => {
  // See if the day_or_page_type is a number stored as a string
  return !!doc.value && !Number.isNaN(+doc.value?.data.day_or_page_type);
});

const title = computed(() => {
  return cms.asText(doc.value?.data?.title);
});

const titleHtml = computed(() => {
  return cms.asHtml(doc.value?.data?.title);
});

const subtitle = computed(() => {
  return cms.asText(doc.value?.data?.subtitle);
});

const titleLayout = "";

const readEndActivityId = computed(() => {
  return `${doc.value.id}.read-end`;
});

const completedActivityIds = computed(() => {
  return currentProgress.value?.completedActivityIds ?? [];
});

const isReadEndActivityComplete = computed(() => {
  return completedActivityIds.value.includes(readEndActivityId.value);
});

const mandatoryActivityIds = computed(() => {
  return doc.value.data.body
    .filter(isMandatoryActivity)
    .map((slice) => sliceToActivityId(slice, doc.value.id));
  // .concat([readEndActivityId.value]);
});

const allActivitiesComplete = computed(() => {
  return !mandatoryActivityIds.value.find(
    (id) => !currentProgress.value?.completedActivityIds.includes(id),
  );
});

const canCompleteDay = computed(() => {
  if (!currentProgress.value || !isPlanStep.value) {
    return false;
  }

  const dayNumber = getDayNumber(doc.value?.data.day_or_page_type);

  return (
    (typeof currentProgress.value.lastCompletedDayIndex === "number" &&
      currentProgress.value.lastCompletedDayIndex === dayNumber - 1) ||
    (typeof currentProgress.value.lastCompletedDayIndex !== "number" && dayNumber === 1)
  );
});

function back() {
  SwipeNavigationInfo.temporarilyDisable();
  window.history.back();
}

async function refreshCurrentProgress() {
  const profileResponse = await axios.get<ProfileApiModel>("/api/profile");
  currentProgress.value = profileResponse.data.developProgress?.currentStrengthProgress;
}

function getCommonActionProperties(actionKind: DevelopUserActionKind): any {
  return {
    id: uuid(),
    dateTimeOffset: moment().format(),
    cmsPageId: doc.value.id,
    strengthItemId: doc.value.data.strength_item_id,
    cmsPlanId: associatedDevelopPlan.value?.cmsPlanId,
    stepRole: getStepRoleFromDayNumber(doc.value.data.day_or_page_type),
    leaderTypeId: null,
    actionKind,
  };
}

async function saveActions(actions: DevelopUserActionApiModel[]): Promise<void> {
  await axios.patch("/api/developuseraction", actions);
}

async function saveAction(action: DevelopUserActionApiModel): Promise<void> {
  await saveActions([action]);
}

onBeforeMount(async () => {
  if (isPlanStep.value) {
    await refreshCurrentProgress();
    if (
      !currentProgress.value ||
      currentProgress.value.strengthItemId !== doc.value.data.strength_item_id
    ) {
      window.location.href = "/develop";
      return;
    }
    const plans = await cms.getDevelopPlans();
    const plan = plans.find((p) => p.strengthItemId === doc.value.data.strength_item_id);
    associatedDevelopPlan.value = plan as DevelopPlan;
    const readPageAction = {
      ...getCommonActionProperties("ReadPage"),
    } as ReadPageActionApiModel;
    await saveAction(readPageAction);
  }
  isLoaded.value = true;
});

const completeButton = ref();

async function completeDay() {
  if ((isPlanStep.value && !canCompleteDay.value) || hasClickedComplete.value) {
    return;
  }

  (completeButton.value as HTMLElement).classList.add("completed");
  const delayPromise = delay(3000);
  hasClickedComplete.value = true;
  const completeStepAction = {
    ...getCommonActionProperties("CompleteStep"),
  } as CompleteStepActionApiModel;
  await saveAction(completeStepAction);
  await delayPromise;
  window.location.href = "/develop";
}

async function handleActivityCompleted(activityId: string) {
  debug("handleActivityCompleted", activityId);
  if (!isPlanStep.value || currentProgress.value?.completedActivityIds.includes(activityId)) {
    return;
  }

  const completeActivityAction = {
    ...getCommonActionProperties("CompleteActivity"),
    activityId,
  } as CompleteActivityActionApiModel;
  await saveAction(completeActivityAction);
  await refreshCurrentProgress();
}

async function handleSelfReviewCompleted(activityId: string, answers: number[]) {
  debug("handleSelfReviewCompleted", activityId, answers);
  if (!isPlanStep.value || currentProgress.value?.completedActivityIds.includes(activityId)) {
    return;
  }

  const completeSelfReviewAction = {
    ...getCommonActionProperties("CompleteSelfReview"),
    activityId,
    answers,
  } as CompleteSelfReviewActionApiModel;
  const completeActivityAction = {
    ...getCommonActionProperties("CompleteActivity"),
    activityId,
  } as CompleteActivityActionApiModel;
  await saveActions([completeSelfReviewAction, completeActivityAction]);
  await refreshCurrentProgress();
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.cms-develop-step-page {
  .plan-title-section {
    background-color: @innovator-color;
  }

  .day-title-section {
    background-color: #eeeeee;
    margin-bottom: 40px;
  }

  section.cms-section:first-child {
    margin-top: 0px;
  }

  @media (min-width: @screen-md-min) {
    .back-button {
      position: absolute;
      margin-left: -130px;
      margin-top: 22px;
    }
  }

  //.bottom-title-image is the default
  .title-image {
    width: 100%;
    max-height: 40vh;
  }

  &.no-title-image {
    .title-image {
      display: none;
    }
  }

  &.right-title-image {
    @media (orientation: landscape) {
      .title-container {
        display: flex;
        flex-direction: row;
        align-items: stretch;

        .title-image {
          width: 40%;

          .cms-img-spacer {
            display: none;
          }
        }
      }
    }
  }

  .read-toolbar-complete-button {
    width: 12.5em; // Give an explicit width so we can animate it easily
    min-width: 3em;
    min-height: 3em;
    position: relative;
    user-select: none !important;
    outline: none !important;

    .tick-icon {
      color: white !important;
      font-size: 32px;
      width: 0;
      overflow: hidden;
      margin-left: 0.3em;
      text-align: left;
      position: absolute;
      top: 0.3em;
      left: 0;
      height: 100%;
    }

    @total-duration: 2s;
    @first-duration: 1.5s;
    @second-duration: @total-duration - @first-duration;
    @text-fade-duration: 0.3s;

    &.completed {
      animation-name: complete-button-reduce, complete-button-to-tick;
      animation-duration: @first-duration, @second-duration;
      animation-delay: 0s, @first-duration;
      animation-iteration-count: 1, 1;
      animation-fill-mode: forwards, forwards;
      animation-timing-function: linear, linear;
      pointer-events: none;

      .read-toolbar-complete-button-text {
        animation-name: fade-out;
        animation-duration: @text-fade-duration;
        animation-fill-mode: forwards;
      }

      .tick-icon {
        animation-name: complete-tick;
        animation-duration: @second-duration;
        animation-timing-function: cubic-bezier(0.68, 0, 0.45, 0.92);
        animation-delay: @first-duration;
        animation-fill-mode: forwards;
      }

      @keyframes fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes fade-out {
        from {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }

      @keyframes complete-tick {
        from {
          width: 0;
          overflow: hidden;
        }
        to {
          width: 1em;
          overflow: visible;
        }
      }

      @keyframes complete-button-reduce {
        from {
          width: 12.5em;
        }
        to {
          width: 34px;
          padding: 0;
        }
      }

      @keyframes complete-button-to-tick {
        from {
          //
        }
        to {
          background-color: @innovator-color;
          border-color: @innovator-color;
          border-radius: 50%;
        }
      }
    }
  }

  .cms-body .container {
    & > * {
      position: relative;

      &::before {
        content: "";
        display: block;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        background-color: rgba(255, 255, 255, 0.8);
        opacity: 0;
        pointer-events: none;
        user-select: none;
        transition: 1s opacity;
        z-index: 1;
      }
    }
  }

  .activity.mandatory:not(.complete) ~ *::before {
    pointer-events: all !important;
    opacity: 1 !important;
  }
}
</style>
